import UploadOneDrive from '../components/UploadOneDrive';
import SelettoreCartella from '../components/SelettoreCartella';
import DocumentiCliente from '../components/DocumentiCliente';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import {
  formatDate, formatEuro, statoLabel, scadenzaColor,
  Modal, ConfirmDialog, LoadingSpinner, Pagination
} from '../components/shared';

function nomeCliente(c) {
  if (!c) return '—';
  if (c.denominazione) return c.denominazione;
  if (c.ragione_sociale) return c.ragione_sociale;
  return [c.nome, c.cognome].filter(Boolean).join(' ') || '—';
}

function Checkbox({ label, checked, onChange }) {
  return (
    <label style={{ display:'flex', alignItems:'center', gap:7, cursor:'pointer', fontSize:13.5 }}>
      <input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)}
        style={{ width:15, height:15, accentColor:'var(--accent)', cursor:'pointer' }} />
      {label}
    </label>
  );
}

function TipiBadges({ c }) {
  const tags = [];
  if (c.societa)        tags.push(['Soc',  'pill-blue']);
  if (c.persona_fisica) tags.push(['PF',   'pill-blue']);
  if (c.corrispettivi)  tags.push(['Corr', 'pill-amber']);
  if (c.privato)        tags.push(['Priv', 'pill-gray']);
  if (c.inps)           tags.push(['INPS', 'pill-amber']);
  if (!tags.length) return <span style={{color:'var(--text-faint)',fontSize:12}}>—</span>;
  return (
    <div style={{ display:'flex', gap:3, flexWrap:'wrap' }}>
      {tags.map(([l,cls]) => <span key={l} className={`pill ${cls}`} style={{fontSize:10,padding:'2px 6px'}}>{l}</span>)}
    </div>
  );
}

// ─── SORTABLE TH ─────────────────────────────────────────────────────────────
function SortTh({ label, field, sort, dir, onSort }) {
  const active = sort === field;
  const arrow = active ? (dir === 'asc' ? ' ↑' : ' ↓') : '';
  return (
    <th onClick={() => onSort(field)}
      style={{
        cursor: 'pointer',
        userSelect: 'none',
        whiteSpace: 'nowrap',
        color: active ? 'var(--accent)' : undefined,
        background: active ? 'var(--accent-light)' : undefined,
      }}>
      {label}{arrow}
    </th>
  );
}

// ─── FORM ─────────────────────────────────────────────────────────────────────
function ClienteForm({ initial = {}, onSave, onClose, loading }) {
  const [d, setD] = useState({
    codice:'', ragione_sociale:'', cognome:'', nome:'', denominazione:'',
    societa:0, persona_fisica:0, corrispettivi:0, privato:0,
    piva:'', cf:'', sede:'', via:'', email:'', pec:'',
    gruppo:'', attivo:1, note:'', inps:0, data_attivazione:'',
    ...initial,
  });
  const set = (k, v) => setD(p => ({ ...p, [k]: v }));
  const buildDenom = () => {
    if (d.societa && d.ragione_sociale) return d.ragione_sociale;
    if (d.persona_fisica) return [d.cognome, d.nome].filter(Boolean).join(' ');
    return d.denominazione;
  };

  const handleImportaVisura = (dati) => {
    setD(p => ({
      ...p,
      ragione_sociale: dati.denominazione || p.ragione_sociale,
      denominazione: dati.denominazione || p.denominazione,
      piva: dati.piva || p.piva,
      cf: dati.cf || p.cf,
      pec: dati.pec || p.pec,
      sede: dati.comune ? `${dati.comune} (${dati.provincia || ''})` : p.sede,
      via: [dati.indirizzo, dati.cap].filter(Boolean).join(' - ') || p.via,
      note: dati.attivita ? ('Attività: ' + dati.attivita) : p.note,
    }));
  };

  return (
    <form onSubmit={e => { e.preventDefault(); onSave({ ...d, denominazione: d.denominazione || buildDenom() }); }}>
      <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:12 }}>
        <ImportaVisura onImporta={handleImportaVisura} />
      </div>
      <div style={{ background:'var(--bg)', borderRadius:'var(--radius-sm)', padding:'12px 16px', marginBottom:18 }}>
        <div className="form-label" style={{ marginBottom:10 }}>Tipologia cliente</div>
        <div style={{ display:'flex', flexWrap:'wrap', gap:20 }}>
          <Checkbox label="Società"        checked={d.societa}        onChange={v => set('societa', v?1:0)} />
          <Checkbox label="Persona Fisica" checked={d.persona_fisica} onChange={v => set('persona_fisica', v?1:0)} />
          <Checkbox label="Corrispettivi"  checked={d.corrispettivi}  onChange={v => set('corrispettivi', v?1:0)} />
          <Checkbox label="Privato"        checked={d.privato}        onChange={v => set('privato', v?1:0)} />
          <Checkbox label="INPS"           checked={d.inps}           onChange={v => set('inps', v?1:0)} />
        </div>
      </div>

      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Codice</label>
          <input className="form-control" value={d.codice||''} onChange={e => set('codice', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Gruppo</label>
          <input className="form-control" value={d.gruppo||''} onChange={e => set('gruppo', e.target.value)} placeholder="Es. PALUMBO, ZIZZO…" />
        </div>

        {!!d.societa && (
          <div className="form-group form-full">
            <label className="form-label">Ragione Sociale</label>
            <input className="form-control" value={d.ragione_sociale||''} onChange={e => set('ragione_sociale', e.target.value)} />
          </div>
        )}

        {!!d.persona_fisica && (<>
          <div className="form-group">
            <label className="form-label">Cognome</label>
            <input className="form-control" value={d.cognome||''} onChange={e => set('cognome', e.target.value.toUpperCase())} />
          </div>
          <div className="form-group">
            <label className="form-label">Nome</label>
            <input className="form-control" value={d.nome||''} onChange={e => set('nome', e.target.value.toUpperCase())} />
          </div>
        </>)}

        <div className="form-group form-full">
          <label className="form-label">Denominazione (display) *</label>
          <input className="form-control" value={d.denominazione||''} onChange={e => set('denominazione', e.target.value)}
            required placeholder={buildDenom() || 'Nome visualizzato nel sistema'} />
        </div>

        <div className="form-group">
          <label className="form-label">Partita IVA</label>
          <input className="form-control" value={d.piva||''} onChange={e => set('piva', e.target.value)} maxLength={11} style={{fontFamily:'monospace'}} />
        </div>
        <div className="form-group">
          <label className="form-label">Codice Fiscale</label>
          <input className="form-control" value={d.cf||''} onChange={e => set('cf', e.target.value.toUpperCase())} maxLength={16} style={{fontFamily:'monospace'}} />
        </div>

        <div className="form-group">
          <label className="form-label">Sede (città)</label>
          <input className="form-control" value={d.sede||''} onChange={e => set('sede', e.target.value.toUpperCase())} />
        </div>
        <div className="form-group">
          <label className="form-label">Via / Indirizzo</label>
          <input className="form-control" value={d.via||''} onChange={e => set('via', e.target.value)} />
        </div>

        <div className="form-group">
          <label className="form-label">Email</label>
          <input className="form-control" type="email" value={d.email||''} onChange={e => set('email', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">PEC</label>
          <input className="form-control" value={d.pec||''} onChange={e => set('pec', e.target.value)} />
        </div>

        <div className="form-group">
          <label className="form-label">Data Attivazione</label>
          <input className="form-control" type="date" value={d.data_attivazione||''} onChange={e => set('data_attivazione', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Stato</label>
          <select className="form-control" value={d.attivo} onChange={e => set('attivo', parseInt(e.target.value))}>
            <option value={1}>Attivo</option>
            <option value={0}>Non attivo</option>
          </select>
        </div>

        <div className="form-group form-full">
          <label className="form-label">Note</label>
          <textarea className="form-control" value={d.note||''} onChange={e => set('note', e.target.value)} rows={3} />
        </div>
      </div>

      <div className="modal-footer" style={{ padding:'16px 0 0', borderTop:'1px solid var(--border)', marginTop:20 }}>
        <button type="button" className="btn" onClick={onClose}>Annulla</button>
        <button type="submit" className="btn btn-primary" disabled={loading}>{loading?'Salvataggio…':'Salva Cliente'}</button>
      </div>
    </form>
  );
}

// ─── ALLEGATI TAB ─────────────────────────────────────────────────────────────
function AllegatiTab({ clienteId, addToast }) {
  const [allegati, setAllegati] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [uploading, setUploading] = useState(false);
  const [delItem, setDelItem]   = useState(null);
  const fileRef = useRef();

  const load = () => {
    setLoading(true);
    api.getAllegati(clienteId).then(setAllegati).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [clienteId]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      await api.uploadAllegato(clienteId, file);
      addToast(`"${file.name}" caricato`, 'success');
      load();
    } catch(err) { addToast(err.message, 'error'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleDelete = async () => {
    try {
      await api.deleteAllegato(clienteId, delItem.filename);
      addToast('Allegato eliminato', 'success');
      setDelItem(null); load();
    } catch(err) { addToast(err.message, 'error'); }
  };

  // Icon based on extension
  const fileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    if (['pdf'].includes(ext)) return '📄';
    if (['jpg','jpeg','png','gif','webp'].includes(ext)) return '🖼️';
    if (['doc','docx'].includes(ext)) return '📝';
    if (['xls','xlsx'].includes(ext)) return '📊';
    if (['zip','rar','7z'].includes(ext)) return '🗜️';
    return '📎';
  };

  // Show clean original name (strip timestamp prefix)
  const cleanName = (filename) => filename.replace(/^\d+_\d+_/, '').replace(/_/g, ' ');

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      {/* Upload area */}
      <div style={{
        border: '2px dashed var(--border-strong)',
        borderRadius: 'var(--radius)',
        padding: '24px',
        textAlign: 'center',
        marginBottom: 20,
        background: 'var(--bg)',
        cursor: 'pointer',
        transition: 'border-color 0.15s',
      }}
        onClick={() => fileRef.current.click()}
        onDragOver={e => e.preventDefault()}
        onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) { const dt = new DataTransfer(); dt.items.add(f); fileRef.current.files = dt.files; handleUpload({ target: { files: [f], value:'' } }); } }}
      >
        <input ref={fileRef} type="file" style={{ display:'none' }} onChange={handleUpload}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.zip,.rar,.txt,.csv" />
        <div style={{ fontSize: 32, marginBottom: 8 }}>📎</div>
        <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-muted)' }}>
          {uploading ? 'Caricamento in corso…' : 'Clicca o trascina un file qui'}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-faint)', marginTop: 4 }}>
          PDF, Word, Excel, immagini, ZIP — max 20MB
        </div>
      </div>

      {/* Files list */}
      {allegati.length === 0 ? (
        <div className="empty-state" style={{ padding: '40px 0' }}>
          <div className="empty-state-icon">📂</div>
          <div className="empty-state-title">Nessun allegato</div>
          <div style={{ fontSize: 13, color: 'var(--text-faint)', marginTop: 4 }}>Carica documenti usando l'area sopra</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {allegati.map(a => (
            <div key={a.filename} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '12px 16px',
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
            }}>
              <span style={{ fontSize: 22, flexShrink: 0 }}>{fileIcon(a.filename)}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {cleanName(a.filename)}
                </div>
              </div>
              <a href={`http://localhost:3001${a.url}`} target="_blank" rel="noreferrer"
                className="btn btn-sm" style={{ flexShrink: 0 }}>
                Apri
              </a>
              <button className="btn btn-sm btn-danger" style={{ flexShrink: 0 }}
                onClick={() => setDelItem(a)}>×</button>
            </div>
          ))}
        </div>
      )}

      {delItem && (
        <ConfirmDialog
          message={`Eliminare l'allegato "${cleanName(delItem.filename)}"?`}
          onConfirm={handleDelete}
          onCancel={() => setDelItem(null)}
        />
      )}
    </div>
  );
}

// ─── LIST ─────────────────────────────────────────────────────────────────────

// ─── IMPORTA VISURA ───────────────────────────────────────────────────────────
function ImportaVisura({ onImporta }) {
  const [loading, setLoading] = React.useState(false);
  const ref = React.useRef();

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('visura', file);
      const res = await fetch('/api/clienti/importa-visura', { method: 'POST', body: fd });
      const j = await res.json();
      if (!j.success) throw new Error(j.error);
      onImporta(j.data);
    } catch(e) {
      alert('Errore: ' + e.message);
    }
    setLoading(false);
    if (ref.current) ref.current.value = '';
  };

  return (
    <>
      <input ref={ref} type="file" accept=".pdf" style={{ display: 'none' }} onChange={handleFile} />
      <button className="btn btn-sm" onClick={() => ref.current.click()} disabled={loading}
        title="Importa dati da visura camerale PDF"
        style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {loading ? '⏳ Lettura…' : '📋 Importa visura'}
      </button>
    </>
  );
}

export function ClientiList({ addToast }) {
  const [data, setData]       = useState({ rows:[], total:0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ]             = useState('');
  const [attivoF, setAttivoF] = useState('');
  const [gruppoF, setGruppoF] = useState('');
  const [page, setPage]       = useState(1);
  const [sort, setSort]       = useState('denominazione');
  const [dir, setDir]         = useState('asc');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [searchParams]        = useSearchParams();
  const navigate              = useNavigate();

  useEffect(() => { if (searchParams.get('new')==='1') setShowForm(true); }, [searchParams]);

  const handleSort = (field) => {
    if (sort === field) setDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSort(field); setDir('asc'); }
    setPage(1);
  };

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, limit:25, sort, dir };
    if (q) params.q = q;
    if (attivoF !== '') params.attivo = attivoF;
    api.getClienti(params).then(d => {
      if (gruppoF) d.rows = d.rows.filter(r => (r.gruppo||'').toLowerCase().includes(gruppoF.toLowerCase()));
      setData(d);
    }).finally(() => setLoading(false));
  }, [q, attivoF, gruppoF, page, sort, dir]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (formData) => {
    setSaving(true);
    try { await api.createCliente(formData); addToast('Cliente creato','success'); setShowForm(false); load(); }
    catch(e) { addToast(e.message,'error'); }
    finally { setSaving(false); }
  };

  const sortProps = { sort, dir, onSort: handleSort };

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <div className="page-title">Clienti</div>
          <div className="page-subtitle">{data.total} clienti totali</div>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button className="btn" onClick={() => navigate('/collega-cartelle')}>☁ Collega cartelle</button>
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>+ Nuovo Cliente</button>
        </div>
      </div>

      <div className="filters-bar">
        <div className="search-box">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5"/><path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
          <input placeholder="Cerca denominazione, CF, P.IVA, sede…" value={q} onChange={e => { setQ(e.target.value); setPage(1); }} />
        </div>
        <input className="form-control" style={{ width:160 }} placeholder="Filtra gruppo…" value={gruppoF} onChange={e => { setGruppoF(e.target.value); setPage(1); }} />
        <select className="form-control" style={{ width:'auto' }} value={attivoF} onChange={e => { setAttivoF(e.target.value); setPage(1); }}>
          <option value="">Tutti gli stati</option>
          <option value="1">Attivi</option>
          <option value="0">Non attivi</option>
        </select>
      </div>

      <div className="card">
        <div className="table-wrap">
          {loading ? <LoadingSpinner /> : (
            <table>
              <thead><tr>
                <SortTh label="Cod."         field="codice"          {...sortProps} />
                <SortTh label="Denominazione" field="denominazione"  {...sortProps} />
                <th>Tipo</th>
                <SortTh label="P.IVA / CF"   field="piva"           {...sortProps} />
                <SortTh label="Sede"          field="sede"           {...sortProps} />
                <th>Email / PEC</th>
                <SortTh label="Gruppo"        field="gruppo"         {...sortProps} />
                <SortTh label="Stato"         field="attivo"         {...sortProps} />
              </tr></thead>
              <tbody>
                {data.rows.map(c => (
                  <tr key={c.id} style={{ cursor:'pointer' }} onClick={() => navigate(`/clienti/${c.id}`)}>
                    <td className="td-mono" style={{ fontSize:11 }}>{c.codice||'—'}</td>
                    <td className="fw-500">{nomeCliente(c)}</td>
                    <td><TipiBadges c={c} /></td>
                    <td className="td-mono">{c.piva||c.cf||'—'}</td>
                    <td>{c.sede||'—'}</td>
                    <td style={{ fontSize:12, color:'var(--c-blue)', maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.email||c.pec||'—'}</td>
                    <td>{c.gruppo ? <span className="pill pill-gray">{c.gruppo}</span> : '—'}</td>
                    <td><span className={`pill ${c.attivo?'pill-green':'pill-gray'}`}>{c.attivo?'Attivo':'Non attivo'}</span></td>
                  </tr>
                ))}
                {data.rows.length === 0 && (
                  <tr><td colSpan={8}><div className="empty-state"><div className="empty-state-icon">👤</div><div className="empty-state-title">Nessun cliente trovato</div></div></td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        <Pagination page={page} total={data.total} limit={25} onPage={setPage} />
      </div>

      {showForm && (
        <Modal title="Nuovo Cliente" onClose={() => setShowForm(false)} size="lg">
          <ClienteForm onSave={handleCreate} onClose={() => setShowForm(false)} loading={saving} />
        </Modal>
      )}
    </div>
  );
}

// ─── DETAIL ───────────────────────────────────────────────────────────────────

// ─── ONEDRIVE TAB ─────────────────────────────────────────────────────────────
function OneDriveTab({ clienteId, addToast }) {
  const [dati, setDati] = useState(null);
  const [suggeriti, setSuggeriti] = useState({});
  const [loading, setLoading] = useState(true);
  const [sfoglia, setSfoglia] = useState(null);   // { anno, inizio }

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/api/clienti/${clienteId}/cartelle`)
      .then(d => {
        setDati(d);
        const collegati = new Set((d.collegamenti || []).map(x => String(x.anno)));
        ['GENERALE', ...(d.anni || [])].forEach(a => {
          const chiave = a === 'GENERALE' ? '0' : a;
          if (collegati.has(chiave)) return;
          api.get(`/api/clienti/${clienteId}/cartelle-suggerisci?anno=${a}`)
            .then(s => setSuggeriti(p => ({ ...p, [a]: s })))
            .catch(() => {});
        });
      })
      .catch(e => addToast(e.message, 'error'))
      .finally(() => setLoading(false));
  }, [clienteId]);  

  useEffect(() => { setSuggeriti({}); load(); }, [load]);

  const salva = async (anno, percorso) => {
    try {
      await api.put(`/api/clienti/${clienteId}/cartelle`, { anno, percorso: percorso || null });
      addToast(percorso ? 'Cartella collegata' : 'Collegamento rimosso', 'success');
      setSfoglia(null);
      load();
    } catch (e) { addToast(e.message, 'error'); }
  };

  if (loading && !dati) return <div style={{ padding:20, color:'var(--text-faint)' }}>⏳ Caricamento…</div>;
  if (!dati) return null;

  const mappa = {};
  (dati.collegamenti || []).forEach(m => { mappa[m.anno === 0 ? 'GENERALE' : String(m.anno)] = m.percorso; });
  const righe = [['GENERALE', 'Generale', 'Documenti permanenti del cliente'], ...(dati.anni || []).map(a => [a, a, 'Documenti dell\'anno'])];

  return (
    <div>
      {dati.errore && (
        <div style={{ padding:'10px 14px', background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:'var(--radius-sm)', color:'#DC2626', fontSize:13, marginBottom:12 }}>
          ⚠ {dati.errore}
        </div>
      )}
      <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:16 }}>
        <thead>
          <tr style={{ background:'var(--bg)', borderBottom:'2px solid var(--border)' }}>
            <th style={{ padding:'8px 12px', textAlign:'left', fontSize:12, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', width:110 }}>Cartella</th>
            <th style={{ padding:'8px 12px', textAlign:'left', fontSize:12, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase' }}>Percorso su OneDrive</th>
            <th style={{ width:210 }}></th>
          </tr>
        </thead>
        <tbody>
          {righe.map(([chiave, titolo, nota]) => {
            const percorso = mappa[chiave];
            const sug = suggeriti[chiave];
            const proposta = !percorso && sug && sug.proposta ? sug : null;
            return (
              <tr key={chiave} style={{ borderBottom:'1px solid var(--border)' }}>
                <td style={{ padding:'10px 12px' }}>
                  <div style={{ fontWeight:700, fontSize:15 }}>{titolo}</div>
                  <div style={{ fontSize:11, color:'var(--text-faint)' }}>{nota}</div>
                </td>
                <td style={{ padding:'10px 12px' }}>
                  {percorso ? (
                    <span style={{ fontSize:13, fontFamily:'monospace', background:'var(--accent-light)', padding:'3px 10px', borderRadius:6, color:'var(--accent)', fontWeight:500 }}>
                      📁 {percorso}
                    </span>
                  ) : proposta ? (
                    <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                      <span style={{ fontSize:12, color:'var(--text-faint)' }}>Proposta:</span>
                      <button onClick={() => salva(chiave, proposta.proposta.percorso)} className="btn btn-sm"
                        style={{ fontWeight: proposta.stato === 'sicuro' ? 600 : 400 }}>
                        ✓ {proposta.proposta.percorso} <span style={{ fontSize:10, opacity:0.6 }}>{Math.round(proposta.proposta.score * 100)}%</span>
                      </button>
                      {(proposta.alternative || []).slice(0, 2).map(a => (
                        <button key={a.percorso} onClick={() => salva(chiave, a.percorso)} className="btn btn-sm" style={{ opacity:0.8 }}>
                          {a.percorso} <span style={{ fontSize:10, opacity:0.6 }}>{Math.round(a.score * 100)}%</span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <span style={{ fontSize:12, color:'var(--text-faint)', fontStyle:'italic' }}>Non collegata</span>
                  )}
                </td>
                <td style={{ padding:'10px 8px', textAlign:'right', whiteSpace:'nowrap' }}>
                  <button className="btn btn-sm" onClick={() => setSfoglia({ anno: chiave, inizio: chiave === 'GENERALE' ? 'Clienti' : chiave })}>
                    📂 {percorso ? 'Cambia' : 'Sfoglia'}
                  </button>
                  {percorso && <button className="btn btn-sm btn-danger" style={{ marginLeft:6 }} onClick={() => salva(chiave, null)}>Rimuovi</button>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ padding:'12px 14px', background:'var(--bg)', borderRadius:'var(--radius-sm)', fontSize:12, color:'var(--text-faint)' }}>
        💡 Le proposte nascono dal nome del cliente. Per collegare tanti clienti insieme usa <b>Collega cartelle</b> nella pagina Clienti.
      </div>

      {sfoglia && (
        <SelettoreCartella inizio={sfoglia.inizio} titolo={`Cartella ${sfoglia.anno === 'GENERALE' ? 'generale' : sfoglia.anno}`}
          onClose={() => setSfoglia(null)} onScegli={(p) => salva(sfoglia.anno, p)} />
      )}
    </div>
  );
}

export function ClienteDetail({ addToast }) {
  const { id }             = useParams();
  const navigate           = useNavigate();
  const [data, setData]    = useState(null);
  const [editing, setEditing]       = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving]         = useState(false);
  const [tab, setTab]      = useState('scadenze');

  const load = () => api.getCliente(id).then(setData).catch(() => navigate('/clienti'));
  useEffect(() => { load(); }, [id]);

  const handleUpdate = async (formData) => {
    setSaving(true);
    try { await api.updateCliente(id, formData); addToast('Cliente aggiornato','success'); setEditing(false); load(); }
    catch(e) { addToast(e.message,'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await api.deleteCliente(id); addToast('Cliente eliminato','success'); navigate('/clienti'); }
    catch(e) { addToast(e.message,'error'); }
  };

  if (!data) return <LoadingSpinner />;
  const { cliente:c, scadenze, banche, attivita } = data;

  const infoRows = [
    ['Codice', c.codice], ['Ragione Sociale', c.ragione_sociale],
    ['Cognome', c.cognome], ['Nome', c.nome],
    ['Partita IVA', c.piva], ['Codice Fiscale', c.cf],
    ['Sede', c.sede], ['Via / Indirizzo', c.via],
    ['Email', c.email], ['PEC', c.pec],
    ['Gruppo', c.gruppo], ['Data Attivazione', c.data_attivazione ? formatDate(c.data_attivazione) : null],
  ].filter(([,v]) => v);

  const TABS = [
    ['scadenze', `Scadenze (${scadenze.length})`],
    ['banche',   `Banche (${banche.length})`],
    ['attivita', `Attività (${attivita.length})`],
    ['allegati', 'Allegati'],
    ['documenti', '📁 Documenti'],
    ['onedrive', '☁ Cartelle'],
  ];

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <button className="btn btn-sm" onClick={() => navigate('/clienti')} style={{ marginBottom:8 }}>← Clienti</button>
          <div className="page-title">{nomeCliente(c)}</div>
          <div style={{ display:'flex', gap:6, marginTop:6, flexWrap:'wrap' }}>
            <TipiBadges c={c} />
            {c.gruppo && <span className="pill pill-gray">{c.gruppo}</span>}
            <span className={`pill ${c.attivo?'pill-green':'pill-gray'}`}>{c.attivo?'Attivo':'Non attivo'}</span>
          </div>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button className="btn" onClick={() => setEditing(true)}>Modifica</button>
          <button className="btn btn-danger" onClick={() => setConfirming(true)}>Elimina</button>
        </div>
      </div>

      <div className="card" style={{ marginBottom:20 }}>
        <div className="card-header"><span className="card-title">Dati Anagrafici</span></div>
        <div className="card-body">
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'12px 28px' }}>
            {infoRows.map(([label, value]) => (
              <div key={label}>
                <div style={{ fontSize:10.5, letterSpacing:'0.4px', textTransform:'uppercase', color:'var(--text-faint)', marginBottom:3 }}>{label}</div>
                <div style={{ fontSize:13.5, fontFamily:['Partita IVA','Codice Fiscale'].includes(label)?'monospace':'inherit' }}>{value}</div>
              </div>
            ))}
          </div>
          {c.note && <div style={{ marginTop:16, padding:'10px 14px', background:'var(--bg)', borderRadius:'var(--radius-sm)', fontSize:13, color:'var(--text-muted)' }}>📝 {c.note}</div>}
        </div>
      </div>

      <div style={{ display:'flex', borderBottom:'1px solid var(--border)', marginBottom:16 }}>
        {TABS.map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            style={{ padding:'10px 18px', border:'none', borderBottom:`2px solid ${tab===key?'var(--accent)':'transparent'}`, background:'none', cursor:'pointer', fontSize:13.5, fontWeight:tab===key?500:400, color:tab===key?'var(--accent)':'var(--text-muted)' }}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'scadenze' && (
        <div className="card"><div className="table-wrap"><table>
          <thead><tr><th>Tipo</th><th>Descrizione</th><th>Scadenza</th><th>Importo</th><th>Stato</th></tr></thead>
          <tbody>
            {scadenze.map(s => (<tr key={s.id}>
              <td><span className="pill pill-gray">{s.tipo}</span></td>
              <td>{s.descrizione||'—'}</td>
              <td style={{fontSize:12}}>{formatDate(s.data_scad)}</td>
              <td style={{fontFamily:'monospace',fontSize:12}}>{s.importo?formatEuro(s.importo):'—'}</td>
              <td><span className={`pill ${scadenzaColor(s.data_scad,s.stato)}`}>{statoLabel(s.stato)}</span></td>
            </tr>))}
            {scadenze.length===0 && <tr><td colSpan={5} style={{textAlign:'center',color:'var(--text-faint)',padding:'24px 0'}}>Nessuna scadenza</td></tr>}
          </tbody>
        </table></div></div>
      )}

      {tab === 'banche' && (
        <div className="card"><div className="table-wrap"><table>
          <thead><tr><th>Banca</th><th>IBAN</th><th>Filiale</th></tr></thead>
          <tbody>
            {banche.map(b => (<tr key={b.id}><td className="fw-500">{b.nome_banca}</td><td className="td-mono">{b.iban}</td><td>{b.filiale||'—'}</td></tr>))}
            {banche.length===0 && <tr><td colSpan={3} style={{textAlign:'center',color:'var(--text-faint)',padding:'24px 0'}}>Nessun conto bancario</td></tr>}
          </tbody>
        </table></div></div>
      )}

      {tab === 'attivita' && (
        <div className="card"><div className="table-wrap"><table>
          <thead><tr><th>Titolo</th><th>Assegnata a</th><th>Scadenza</th><th>Avanz.</th><th>Stato</th></tr></thead>
          <tbody>
            {attivita.map(a => (<tr key={a.id}>
              <td className="fw-500">{a.titolo}</td>
              <td>{a.assegnata_a||'—'}</td>
              <td style={{fontSize:12}}>{formatDate(a.data_scad)}</td>
              <td><div style={{display:'flex',alignItems:'center',gap:8}}>
                <div style={{flex:1,height:4,background:'var(--border)',borderRadius:2,overflow:'hidden',minWidth:60}}>
                  <div style={{width:`${a.perc_compl}%`,height:'100%',background:'var(--accent)'}} />
                </div>
                <span style={{fontSize:11,color:'var(--text-faint)',minWidth:28}}>{a.perc_compl}%</span>
              </div></td>
              <td><span className={`pill ${a.stato==='COMPLETATA'?'pill-green':a.stato==='IN_CORSO'?'pill-amber':'pill-blue'}`}>{statoLabel(a.stato)}</span></td>
            </tr>))}
            {attivita.length===0 && <tr><td colSpan={5} style={{textAlign:'center',color:'var(--text-faint)',padding:'24px 0'}}>Nessuna attività</td></tr>}
          </tbody>
        </table></div></div>
      )}

      {tab === 'allegati' && (
        <div className="card">
          <div className="card-header"><span className="card-title">Documenti allegati</span></div>
          <div className="card-body">
            <AllegatiTab clienteId={id} addToast={addToast} />
          </div>
        </div>
      )}

      {tab === 'documenti' && (
        <div className="card">
          <div className="card-header"><span className="card-title">📁 Documenti su OneDrive</span></div>
          <div className="card-body">
            <DocumentiCliente clienteId={id} addToast={addToast} />
          </div>
        </div>
      )}

      {tab === 'onedrive' && (
        <div className="card">
          <div className="card-header"><span className="card-title">☁ Cartelle OneDrive collegate</span></div>
          <div className="card-body">
            <OneDriveTab clienteId={id} addToast={addToast} />
          </div>
        </div>
      )}

      {editing && <Modal title="Modifica Cliente" onClose={() => setEditing(false)} size="lg">
        <ClienteForm initial={c} onSave={handleUpdate} onClose={() => setEditing(false)} loading={saving} />
      </Modal>}
      {confirming && <ConfirmDialog message={`Eliminare "${nomeCliente(c)}"?`} onConfirm={handleDelete} onCancel={() => setConfirming(false)} />}
    </div>
  );
}
